/**
 * Command validation and application. Every handler validates fully before mutating anything,
 * so a rejected command leaves the state untouched.
 */
import { BALANCE } from '../balance';
import { hexDistance, hexEquals, type Axial } from '../hex';
import { clockOf } from '../time';
import {
  UNIT_TYPES,
  type Army,
  type ArmyCommand,
  type Command,
  type CommandResult,
  type GameEvent,
  type MatchState,
  type Player,
  type TeamId,
  type UnitCounts,
} from '../types';
import {
  armiesAt,
  createArmy,
  deleteArmy,
  heroArmy,
  isRecruitSite,
  markHandled,
  keepOf,
  placeName,
  teamPlayers,
  totalUnits,
  troopsOf,
} from './armies';
import { emit, newId, type Ctx } from './ctx';
import { buildMinutes, isAtWorksite, nextRecruitSlot, recruitMinutes } from './economy';
import { hexAt, inBounds } from './grid';
import { dispatchMessenger, estimateTravel, messengerOrigin } from './messengers';
import { executeArmyCommand } from './orders';
import { isPassable, pathForArmy } from './pathfinding';
import { concedeMatch } from './match';
import { joinRunningMatch } from './players';
import { giveRelicToArmy, relicHolder, setRelicInKeep, setRelicOnHex } from './relics';
import type { BuildKind } from './sim-state';
import { castSpell, spellError } from './spells';

type Outcome = { ok: true; eta?: number } | { ok: false; error: string };

const fail = (error: string): Outcome => ({ ok: false, error });
const OK: Outcome = { ok: true };

export function handleCommand(ctx: Ctx, playerId: string, command: Command): CommandResult {
  const outcome = route(ctx, playerId, command);
  if (!outcome.ok) return outcome;
  (ctx.sim.lastCommandAt ??= {})[playerId] = ctx.state.now;
  const events: GameEvent[] = [...ctx.events];
  return outcome.eta === undefined ? { ok: true, events } : { ok: true, events, eta: outcome.eta };
}

const LOBBY_COMMANDS = new Set<Command['type']>([
  'chat',
  'setPreferences',
  'setQuietWindow',
  'changeTeam',
  'updateCharacter',
]);
const LOBBY_ONLY = new Set<Command['type']>(['changeTeam', 'updateCharacter']);

function route(ctx: Ctx, playerId: string, command: Command): Outcome {
  const { state } = ctx;
  const player = state.players[playerId];
  if (!player) return fail('You are not in this match.');
  if (state.status === 'finished') return fail('The match is over.');
  if (state.status === 'lobby' && !LOBBY_COMMANDS.has(command.type))
    return fail('The match has not started yet.');
  if (state.status !== 'lobby' && LOBBY_ONLY.has(command.type))
    return fail('That can only be changed in the lobby.');
  switch (command.type) {
    case 'moveArmy':
    case 'retreat':
    case 'setDoctrine':
    case 'setConditionals':
      return armyOrder(ctx, player, command);
    case 'splitArmy':
      return splitArmy(ctx, player, command);
    case 'mergeArmies':
      return mergeArmies(ctx, player, command.armyId, command.intoArmyId);
    case 'recruit':
      return recruit(ctx, player, command);
    case 'build':
      return build(ctx, player, command.armyId, command.kind, command.at);
    case 'demolish':
      return build(
        ctx,
        player,
        command.armyId,
        command.kind === 'road' ? 'demolishRoad' : 'burnBridge',
        command.at,
      );
    case 'castSpell': {
      const error = spellError(state, player, command.spell, command.target);
      if (error) return fail(error);
      castSpell(ctx, player, command.spell, command.target);
      return OK;
    }
    case 'pickUpRelic':
      return pickUpRelic(ctx, player, command.armyId, command.relicId);
    case 'dropRelic':
      return dropRelic(ctx, player, command.armyId, command.relicId);
    case 'channel':
      return channel(ctx, player, command.on);
    case 'approveJoin':
    case 'rejectJoin':
      return decideJoin(ctx, player, command.requestId, command.type === 'approveJoin');
    case 'ping':
      return ping(ctx, player, command);
    case 'chat':
      emit(ctx, 'chat', {
        visibleTo: [player.teamId],
        text: `${player.name}: ${command.text.trim()}`,
        data: { playerId: player.id, text: command.text.trim() },
      });
      return OK;
    case 'setQuietWindow':
      return setQuietWindow(ctx, player, command.startHour);
    case 'setPreferences': {
      const p = command.preferences;
      player.preferences = {
        dispatchHour: p.dispatchHour ?? player.preferences.dispatchHour,
        utcOffsetMinutes: p.utcOffsetMinutes ?? player.preferences.utcOffsetMinutes,
        alerts: { ...player.preferences.alerts, ...p.alerts },
      };
      return OK;
    }
    case 'renameArmy':
      return renameArmy(state, player, command.armyId, command.name);
    case 'changeTeam':
      return changeTeam(state, player, command.teamId);
    case 'updateCharacter':
      player.character = { ...command.character };
      return OK;
    case 'voteConcede':
      return voteConcede(ctx, player, true);
    case 'withdrawConcede':
      return voteConcede(ctx, player, false);
  }
}

// ---------------------------------------------------------------------------------------------
// Names, teams and the mercy vote
// ---------------------------------------------------------------------------------------------

function renameArmy(state: MatchState, player: Player, armyId: string, raw: string): Outcome {
  const army = ownArmy(state, player, armyId);
  if (!army) return fail('No such army.');
  if (army.heroId && army.heroId !== player.id) {
    return fail(`${state.players[army.heroId]?.name ?? 'Another player'} leads that army.`);
  }
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < 1 || name.length > 24) return fail('Army names are 1–24 characters.');
  const taken = Object.values(state.armies).some(
    (a) => a.id !== army.id && a.teamId === army.teamId && a.name.toLowerCase() === name.toLowerCase(),
  );
  if (taken) return fail('Another of your armies already has that name.');
  army.name = name;
  markHandled(state, army.id, player.id);
  return OK;
}

function changeTeam(state: MatchState, player: Player, teamId: TeamId): Outcome {
  if (player.teamId === teamId) return fail('You are already on that team.');
  const members = Object.values(state.players).filter((p) => p.teamId === teamId).length;
  if (members >= state.config.teamSlots[teamId]) return fail(`${state.teams[teamId].name} is full.`);
  player.teamId = teamId;
  return OK;
}

/** Human players on a team: only they vote in the mercy vote. */
function humanVoters(state: MatchState, team: TeamId): Player[] {
  return Object.values(state.players)
    .filter((p) => p.teamId === team && !p.isAi)
    .sort((a, b) => (a.id < b.id ? -1 : 1));
}

function voteConcede(ctx: Ctx, player: Player, voted: boolean): Outcome {
  const { state, sim } = ctx;
  if (player.isAi) return fail('AI captains do not vote.');
  const team = player.teamId;
  const votes = (sim.concedeVotes ??= [[], []]);
  const since = (sim.concedeSince ??= [null, null]);
  const has = votes[team].includes(player.id);
  if (voted === has)
    return fail(voted ? 'You have already voted to concede.' : 'You have not voted to concede.');
  votes[team] = voted ? [...votes[team], player.id] : votes[team].filter((id) => id !== player.id);
  since[team] = votes[team].length === 0 ? null : (since[team] ?? state.now);
  const voters = humanVoters(state, team);
  const count = voters.filter((p) => votes[team].includes(p.id)).length;
  emit(ctx, 'concedeVote', {
    visibleTo: [team],
    concerns: voters.filter((p) => p.id !== player.id).map((p) => p.id),
    text: voted
      ? `${player.name} voted to concede (${count} of ${voters.length}). Everyone must agree.`
      : `${player.name} withdrew their vote to concede (${count} of ${voters.length}).`,
    data: { playerId: player.id, voted, votes: count, needed: voters.length },
  });
  if (voted && count === voters.length) concedeMatch(ctx, team);
  return OK;
}

// ---------------------------------------------------------------------------------------------
// Army orders (instant or by messenger)
// ---------------------------------------------------------------------------------------------

function stripVia(command: ArmyCommand): ArmyCommand {
  switch (command.type) {
    case 'moveArmy':
      return { type: 'moveArmy', armyId: command.armyId, to: { q: command.to.q, r: command.to.r } };
    case 'retreat':
      return { type: 'retreat', armyId: command.armyId };
    case 'setDoctrine':
      return { type: 'setDoctrine', armyId: command.armyId, doctrine: command.doctrine };
    case 'setConditionals':
      return {
        type: 'setConditionals',
        armyId: command.armyId,
        orders: command.orders.map((o) => ({ trigger: { ...o.trigger }, action: { ...o.action } })),
      };
  }
}

function ownArmy(state: MatchState, player: Player, armyId: string): Army | null {
  const army = state.armies[armyId];
  return army && army.teamId === player.teamId ? army : null;
}

function validDestination(state: MatchState, to: Axial): string | null {
  if (!inBounds(state.map, to)) return 'That hex is off the map.';
  if (!isPassable(hexAt(state.map, to))) return 'Armies cannot go there.';
  return null;
}

function validateArmyOrder(state: MatchState, army: Army, command: ArmyCommand): string | null {
  switch (command.type) {
    case 'moveArmy': {
      const bad = validDestination(state, command.to);
      if (bad) return bad;
      if (hexEquals(command.to, army.pos) && !army.engagementId) return 'The army is already there.';
      if (!pathForArmy(state.map, army, command.to)) return 'There is no route there.';
      return null;
    }
    case 'setConditionals': {
      if (command.orders.length > BALANCE.maxConditionals) {
        return `An army can hold at most ${BALANCE.maxConditionals} standing orders.`;
      }
      for (const o of command.orders) {
        if (o.action.kind === 'moveTo' || o.action.kind === 'fallBackTo') {
          const bad = validDestination(state, o.action.to);
          if (bad) return bad;
        }
      }
      return null;
    }
    default:
      return null;
  }
}

/** Whether an order reaches the army at once: hero present, or doctrine near the keep. */
export function isInstantOrder(state: MatchState, player: Player, army: Army, command: ArmyCommand): boolean {
  if (army.heroId === player.id) return true;
  const hero = heroArmy(state, player.id);
  if (hero && player.heroStatus === 'active' && hexEquals(hero.pos, army.pos)) return true;
  return (
    command.type === 'setDoctrine' &&
    hexDistance(army.pos, keepOf(state, player.teamId)) <= BALANCE.instantDoctrineRadius
  );
}

function armyOrder(ctx: Ctx, player: Player, command: ArmyCommand & { via?: 'auto' | 'raven' }): Outcome {
  const { state } = ctx;
  const army = ownArmy(state, player, command.armyId);
  if (!army) return fail('No such army.');
  if (army.heroId && army.heroId !== player.id && command.type !== 'setDoctrine') {
    const owner = state.players[army.heroId]?.name ?? 'Another player';
    return fail(`${owner} leads that army; you can only change its doctrine.`);
  }
  const order = stripVia(command);
  const invalid = validateArmyOrder(state, army, order);
  if (invalid) return fail(invalid);

  if (isInstantOrder(state, player, army, order)) {
    const error = executeArmyCommand(ctx, army, order, player.id);
    return error ? fail(error) : OK;
  }
  const via = command.via === 'raven' ? 'raven' : 'messenger';
  const team = state.teams[player.teamId];
  if (via === 'raven' && team.mana < BALANCE.ravenManaCost) {
    return fail(`A raven needs ${BALANCE.ravenManaCost} mana.`);
  }
  if (estimateTravel(state, via, messengerOrigin(state, player), army.pos, player.teamId) === null) {
    return fail('No messenger can reach that army.');
  }
  if (via === 'raven') team.mana -= BALANCE.ravenManaCost;
  const messenger = dispatchMessenger(ctx, player, army, order, via);
  if (typeof messenger === 'string') return fail(messenger);
  return { ok: true, eta: messenger.eta };
}

// ---------------------------------------------------------------------------------------------
// Split & merge
// ---------------------------------------------------------------------------------------------

/** Heroes reorganise armies where they stand; anyone may at their own keep. */
function canReorganise(state: MatchState, player: Player, pos: Axial): boolean {
  const hero = heroArmy(state, player.id);
  if (hero && player.heroStatus === 'active' && hexEquals(hero.pos, pos)) return true;
  return hexEquals(pos, keepOf(state, player.teamId));
}

function splitArmy(
  ctx: Ctx,
  player: Player,
  command: { armyId: string; units: UnitCounts; withHero: boolean; name?: string },
): Outcome {
  const { state } = ctx;
  const army = ownArmy(state, player, command.armyId);
  if (!army) return fail('No such army.');
  if (army.engagementId) return fail('Armies cannot split in the middle of a fight.');
  if (army.heroId && army.heroId !== player.id) return fail('Only its hero can split that army.');
  if (!canReorganise(state, player, army.pos))
    return fail('Your hero must be with the army (or at the keep) to split it.');
  if (command.withHero && army.heroId !== player.id) return fail('Your hero is not in that army.');
  for (const t of UNIT_TYPES) {
    if (command.units[t] < 0 || command.units[t] > army.units[t])
      return fail(`Not enough ${t} to split off.`);
  }
  const moving = totalUnits(command.units);
  if (moving === 0 && !command.withHero) return fail('Choose some troops to split off.');
  const leftBehind = troopsOf(army) - moving;
  const heroStays = army.heroId !== null && !command.withHero;
  if (leftBehind === 0 && !heroStays) return fail('Leave some troops behind, or simply move the army.');

  const share = troopsOf(army) > 0 ? moving / troopsOf(army) : 0;
  const woundedMoving = Math.round(army.wounded * share);
  for (const t of UNIT_TYPES) army.units[t] -= command.units[t];
  army.wounded -= woundedMoving;
  const split = createArmy(ctx, {
    createdBy: player.id,
    teamId: army.teamId,
    pos: army.pos,
    units: command.units,
    heroId: command.withHero ? player.id : null,
    name: command.name,
    doctrine: army.doctrine,
    morale: army.morale,
  });
  split.wounded = woundedMoving;
  markHandled(state, army.id, player.id);
  split.supplied = army.supplied;
  split.shakenUntil = army.shakenUntil;
  if (command.withHero) {
    army.heroId = null;
    army.channelling = false;
  }
  return OK;
}

function mergeArmies(ctx: Ctx, player: Player, fromId: string, intoId: string): Outcome {
  const { state, sim } = ctx;
  const from = ownArmy(state, player, fromId);
  const into = ownArmy(state, player, intoId);
  if (!from || !into) return fail('No such army.');
  if (from.id === into.id) return fail('An army cannot merge with itself.');
  if (!hexEquals(from.pos, into.pos)) return fail('Armies must stand on the same hex to merge.');
  if (from.engagementId || into.engagementId) return fail('Armies cannot merge in the middle of a fight.');
  if (from.heroId && into.heroId) return fail('Two heroes cannot share one army.');
  for (const a of [from, into]) {
    if (a.heroId && a.heroId !== player.id) return fail('Only its hero can merge that army.');
  }
  if (!canReorganise(state, player, from.pos))
    return fail('Your hero must be with the armies (or at the keep) to merge them.');
  if (troopsOf(from) + troopsOf(into) > BALANCE.armyCap) {
    return fail(`Merged armies cannot exceed ${BALANCE.armyCap} troops.`);
  }
  const total = troopsOf(from) + troopsOf(into);
  into.morale =
    total > 0 ? (from.morale * troopsOf(from) + into.morale * troopsOf(into)) / total : into.morale;
  for (const t of UNIT_TYPES) into.units[t] += from.units[t];
  into.wounded += from.wounded;
  into.heroId = into.heroId ?? from.heroId;
  into.supplied = into.supplied && from.supplied;
  for (const relicId of [...from.relicIds]) {
    const relic = state.relics[relicId];
    if (relic) giveRelicToArmy(state, relic, into);
  }
  for (const m of Object.values(state.messengers)) if (m.targetArmyId === from.id) m.targetArmyId = into.id;
  markHandled(state, into.id, player.id);
  sim.jobs = sim.jobs.filter((j) => j.armyId !== from.id);
  deleteArmy(state, from.id);
  return OK;
}

// ---------------------------------------------------------------------------------------------
// Economy commands
// ---------------------------------------------------------------------------------------------

function recruit(
  ctx: Ctx,
  player: Player,
  command: { at: Axial; unit: keyof UnitCounts; count: number },
): Outcome {
  const { state, sim } = ctx;
  const team = state.teams[player.teamId];
  if (!inBounds(state.map, command.at)) return fail('That hex is off the map.');
  if (!isRecruitSite(state, player.teamId, command.at))
    return fail('Recruit at your keep or a village you hold.');
  if (command.count < 1 || command.count > 100 || !Number.isInteger(command.count))
    return fail('Recruit 1–100 at a time.');
  if (armiesAt(state, command.at).some((a) => a.teamId !== player.teamId && troopsOf(a) > 0)) {
    return fail('Enemies hold that place.');
  }
  const cost = BALANCE.unitStats[command.unit].cost * command.count;
  if (team.food < cost) return fail(`Not enough food (needs ${cost}).`);
  team.food -= cost;
  const readyAt =
    nextRecruitSlot(state, player.teamId, command.at) + recruitMinutes(command.unit, command.count);
  sim.recruits.push({
    id: newId(state, 'rq'),
    teamId: player.teamId,
    playerId: player.id,
    at: { ...command.at },
    unit: command.unit,
    count: command.count,
    readyAt,
  });
  return { ok: true, eta: readyAt };
}

const BUILD_COST: Record<BuildKind, number> = {
  road: BALANCE.build.road.food,
  bridge: BALANCE.build.bridge.food,
  watchtower: BALANCE.build.watchtower.food,
  demolishRoad: 0,
  burnBridge: 0,
};

function buildError(state: MatchState, kind: BuildKind, at: Axial): string | null {
  const hex = hexAt(state.map, at);
  if (!hex) return 'That hex is off the map.';
  const land = hex.terrain !== 'water' && isPassable(hex);
  switch (kind) {
    case 'road':
      if (!land) return 'Roads need passable land.';
      return hex.road ? 'There is already a road.' : null;
    case 'bridge':
      if (hex.terrain !== 'water') return 'Bridges span rivers.';
      return hex.bridge ? 'There is already a bridge.' : null;
    case 'watchtower':
      if (!land) return 'Watchtowers need passable land.';
      return hex.feature ? 'Something already stands there.' : null;
    case 'demolishRoad':
      return hex.road && hex.terrain !== 'water' ? null : 'There is no road to tear up.';
    case 'burnBridge':
      return hex.bridge ? null : 'There is no bridge to burn.';
  }
}

function build(ctx: Ctx, player: Player, armyId: string, kind: BuildKind, at: Axial): Outcome {
  const { state, sim } = ctx;
  const army = ownArmy(state, player, armyId);
  if (!army) return fail('No such army.');
  if (army.engagementId) return fail('The army is fighting.');
  if (troopsOf(army) === 0 && !army.heroId) return fail('The army has no hands to work with.');
  if (!canReorganise(state, player, army.pos))
    return fail('Your hero must be with that army to direct the work.');
  const invalid = buildError(state, kind, at);
  if (invalid) return fail(invalid);
  if (!isAtWorksite(kind, army, at)) {
    return fail(
      kind === 'bridge' || kind === 'burnBridge'
        ? 'The army must be beside the river crossing.'
        : 'The army must stand on that hex.',
    );
  }
  if (sim.jobs.some((j) => hexEquals(j.at, at) && j.kind === kind))
    return fail('That work is already under way.');
  const team = state.teams[player.teamId];
  const cost = BUILD_COST[kind];
  if (team.food < cost) return fail(`Not enough food (needs ${cost}).`);
  team.food -= cost;
  army.route = [];
  army.moveProgress = 0;
  const doneAt = state.now + buildMinutes(kind);
  sim.jobs.push({
    id: newId(state, 'j'),
    teamId: player.teamId,
    playerId: player.id,
    kind,
    at: { ...at },
    armyId,
    doneAt,
  });
  return { ok: true, eta: doneAt };
}

// ---------------------------------------------------------------------------------------------
// Relics, channelling
// ---------------------------------------------------------------------------------------------

function pickUpRelic(ctx: Ctx, player: Player, armyId: string, relicId: string): Outcome {
  const { state } = ctx;
  const army = ownArmy(state, player, armyId);
  const relic = state.relics[relicId];
  if (!army || !relic) return fail('No such army or relic.');
  if (army.engagementId) return fail('The army is fighting.');
  const loc = relic.location;
  const here =
    (loc.kind === 'hex' && hexEquals(loc.pos, army.pos)) ||
    (loc.kind === 'keep' && loc.teamId === army.teamId && hexEquals(keepOf(state, army.teamId), army.pos));
  if (!here) return fail('The relic is not here.');
  const holder = relicHolder(state, relic);
  if (holder !== null && holder !== army.teamId)
    return fail('The enemy holds that relic; drive them off first.');
  giveRelicToArmy(state, relic, army);
  return OK;
}

function dropRelic(ctx: Ctx, player: Player, armyId: string, relicId: string): Outcome {
  const { state } = ctx;
  const army = ownArmy(state, player, armyId);
  const relic = state.relics[relicId];
  if (!army || !relic) return fail('No such army or relic.');
  if (!army.relicIds.includes(relicId)) return fail('That army is not carrying the relic.');
  if (army.engagementId) return fail('The army is fighting.');
  if (hexEquals(army.pos, keepOf(state, army.teamId))) setRelicInKeep(state, relic, army.teamId);
  else setRelicOnHex(state, relic, army.pos, army.teamId);
  return OK;
}

function channel(ctx: Ctx, player: Player, on: boolean): Outcome {
  const { state } = ctx;
  const hero = heroArmy(state, player.id);
  if (!hero || player.heroStatus !== 'active') return fail('Your hero is not in the field.');
  if (!on) {
    hero.channelling = false;
    return OK;
  }
  const f = hexAt(state.map, hero.pos)?.feature;
  if (f?.kind !== 'leyNode' || f.ownerTeam !== player.teamId)
    return fail('Channel at a ley node your team holds.');
  if (hero.engagementId) return fail('Not while fighting.');
  hero.channelling = true;
  hero.route = [];
  hero.moveProgress = 0;
  return OK;
}

// ---------------------------------------------------------------------------------------------
// Team actions
// ---------------------------------------------------------------------------------------------

function decideJoin(ctx: Ctx, player: Player, requestId: string, approve: boolean): Outcome {
  const { state } = ctx;
  const request = state.joinRequests.find((r) => r.id === requestId);
  if (!request || request.teamId !== player.teamId) return fail('No such join request.');
  if (request.status !== 'pending') return fail('That request has already been decided.');
  if (approve && state.players[request.id]) return fail('That player is already in the match.');
  request.status = approve ? 'approved' : 'rejected';
  request.decidedBy = player.id;
  if (approve) {
    joinRunningMatch(ctx, {
      id: request.id,
      name: request.name,
      teamId: request.teamId,
      character: request.character,
      isAi: false,
      isHost: false,
    });
  }
  return OK;
}

const PING_TEXT = {
  help: 'needs reinforcements',
  scout: 'asks for scouting',
  escort: 'asks for an escort',
  attack: 'calls for an attack',
} as const;

function ping(
  ctx: Ctx,
  player: Player,
  command: { at: Axial; kind: keyof typeof PING_TEXT; note?: string },
): Outcome {
  const { state } = ctx;
  if (!inBounds(state.map, command.at)) return fail('That hex is off the map.');
  const note = command.note?.trim();
  const teammates = teamPlayers(state, player.teamId)
    .filter((p) => p.id !== player.id)
    .map((p) => p.id);
  const event = emit(ctx, 'ping', {
    visibleTo: [player.teamId],
    concerns: teammates,
    text: `${player.name} ${PING_TEXT[command.kind]} ${placeName(state, command.at)}${note ? `: “${note}”` : '.'}`,
    pos: command.at,
    data: { kind: command.kind, playerId: player.id, note: note || null },
  });
  const sim = ctx.sim;
  sim.pings = [
    ...(sim.pings ?? []).filter((p) => state.now - p.time < BALANCE.pingLifetimeMinutes),
    {
      id: event.id,
      teamId: player.teamId,
      playerId: player.id,
      kind: command.kind,
      at: { q: command.at.q, r: command.at.r },
      note: note || null,
      time: state.now,
    },
  ];
  return OK;
}

function setQuietWindow(ctx: Ctx, player: Player, startHour: number | null): Outcome {
  const team = ctx.state.teams[player.teamId];
  if (startHour !== null && (!Number.isInteger(startHour) || startHour < 0 || startHour > 23)) {
    return fail('Pick an hour from 0 to 23.');
  }
  team.quietWindowStart = startHour;
  const end = startHour === null ? null : (startHour + BALANCE.quietWindowHours) % 24;
  emit(ctx, 'chat', {
    visibleTo: [player.teamId],
    text:
      startHour === null || end === null
        ? `${player.name} cleared the quiet window.`
        : `${player.name} set the quiet window to ${clockOf((startHour - 8) * 60 + 24 * 60)}–${clockOf((end - 8) * 60 + 24 * 60)}.`,
    data: { playerId: player.id },
  });
  return OK;
}
